/* Kook cart deposits. Eligibility comes only from Liquid collection membership. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.KookRentalDeposit = api;
    api.start(root);
  }
})(typeof window === 'undefined' ? this : window, function () {
  'use strict';

  function selectTier(subtotal) {
    if (!Number.isSafeInteger(subtotal) || subtotal < 0) throw new Error('invalid-state');
    if (subtotal === 0) return null;
    if (subtotal <= 10000) return '200';
    if (subtotal <= 35000) return '400';
    if (subtotal <= 95000) return '600';
    return '1000';
  }

  function buildUpdates(state) {
    const value = selectTier(state.rentalSubtotal);
    if (!Array.isArray(state.deposits) || !Array.isArray(state.variants)) throw new Error('invalid-state');
    let selected = null;
    if (value) {
      // The existing thresholds are in AED; never apply them to another currency.
      if (state.currency !== 'AED') throw new Error('currency');
      const matches = state.variants.filter(variant => String(variant.value).trim() === value);
      if (matches.length !== 1 || !matches[0].available || !matches[0].id) throw new Error('unavailable');
      selected = matches[0];
    }
    const updates = {};
    let kept = false;
    for (const line of state.deposits) {
      if (!line.key || !Number.isSafeInteger(line.quantity)) throw new Error('invalid-state');
      if (selected && String(line.variantId) === String(selected.id) && !kept) {
        kept = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      } else updates[line.key] = 0;
    }
    if (selected && !kept) updates[String(selected.id)] = 1;
    return updates;
  }

  async function reconcileCart(io, seed) {
    let state = seed || await io.readState();
    let changed = false;
    // Verify server state after each mutation; bound retries if another app keeps changing it.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const updates = buildUpdates(state);
      if (!Object.keys(updates).length) return { changed: changed, state: state };
      await io.writeUpdates(updates);
      changed = true;
      state = await io.readState();
    }
    if (Object.keys(buildUpdates(state)).length) throw new Error('not-settled');
    return { changed: changed, state: state };
  }

  function createQueue(onBusy) {
    let tail = Promise.resolve();
    let pending = 0;
    function enqueue(work) {
      pending += 1;
      onBusy(true);
      const task = tail.then(work);
      tail = task.catch(function () {});
      return task.finally(function () {
        pending -= 1;
        if (pending === 0) onBusy(false);
      });
    }
    enqueue.idle = function idle() {
      const current = tail;
      return current.then(function () { if (current !== tail) return idle(); });
    };
    return enqueue;
  }

  function start(win) {
    const doc = win.document;
    const seedElement = doc && doc.getElementById('KookDepositState');
    if (!seedElement || seedElement.dataset.initialized) return;
    seedElement.dataset.initialized = 'true';
    const routeRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    const cartUrl = routeRoot + 'cart';
    const nativeFetch = win.fetch.bind(win);
    const checkoutSelector = '.cart__checkout, [name="checkout"]';
    let busy = false;
    let error = null;
    let lastCartSignature = '';
    let resumingForm = null;
    let resumedEvent = null;
    let checkoutPending = false;
    let checkoutDirty = false;
    let needsRefresh = false;
    let verifiedSignature = null;
    let cartRevision = 0;
    const syncingSignatures = new Set();
    const removingKeys = new Set();
    const cartControlStates = new Map();
    const checkoutInputStates = new Map();

    function releaseCheckout() {
      checkoutInputStates.forEach(function (disabled, input) { input.disabled = disabled; });
      checkoutInputStates.clear();
      lockCartControls(false);
      checkoutPending = false;
    }

    function lockCartControls(locked) {
      if (locked) {
        doc.querySelectorAll('form[data-location] .js-qty__num, form[data-location] .js-qty__adjust').forEach(function (control) {
          if (!cartControlStates.has(control)) cartControlStates.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        cartControlStates.forEach(function (disabled, control) { control.disabled = disabled; });
        cartControlStates.clear();
      }
    }

    function display() {
      doc.querySelectorAll('[data-kook-deposit-status]').forEach(function (status) {
        const message = status.querySelector('[data-kook-deposit-message]');
        const retry = status.querySelector('[data-kook-deposit-retry]');
        status.hidden = !busy && !error;
        if (message) message.textContent = error || (busy ? 'Updating your cart…' : '');
        if (retry) retry.hidden = busy || !error;
      });
      doc.querySelectorAll(checkoutSelector).forEach(function (button) {
        // BTA owns the native disabled property while checking reservations.
        // Restoring a saved value can overwrite its later enable/disable state.
        // The capture-phase submit guard below owns deposit authorization.
        if (busy || error) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        button.setAttribute('aria-busy', String(busy));
      });
      doc.querySelectorAll('form[data-location]').forEach(function (form) {
        form.setAttribute('aria-busy', String(busy));
      });
    }

    function report(failure, scope) {
      needsRefresh = true;
      if (failure.message === 'currency') error = 'Rental deposits are calculated in AED. Please use AED or contact Kook to complete this rental.';
      else if (failure.message === 'unavailable') error = 'Your rental deposit is unavailable. Please contact Kook before checking out.';
      else if (scope === 'markup') error = 'We could not refresh your cart. Please retry.';
      else error = 'We could not update your rental deposit. Please retry before checking out.';
      doc.querySelectorAll(checkoutSelector).forEach(button => button.classList.remove('btn--loading'));
      display();
    }

    function failure(code, status) {
      const problem = new Error(code);
      if (status !== undefined) problem.status = status;
      return problem;
    }

    function transient(problem) {
      return problem.message === 'network' || problem.message === 'invalid-response' ||
        problem.message === 'markup-unavailable' || problem.status === 408 || problem.status === 429 || problem.status >= 500;
    }

    function pause(attempt) {
      return new Promise(resolve => win.setTimeout(resolve, attempt === 0 ? 150 : 450));
    }

    async function retryRead(read) {
      for (let attempt = 0; ; attempt += 1) {
        try { return await read(); }
        catch (problem) {
          if (attempt >= 2 || !transient(problem)) throw problem;
          await pause(attempt);
        }
      }
    }

    async function fetchResponse(path, options) {
      let response;
      const controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      const timer = controller ? win.setTimeout(() => controller.abort(), 8000) : null;
      try { response = await nativeFetch(path, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, options, controller ? { signal: controller.signal } : {})); }
      catch (_) { throw failure('network'); }
      finally { if (timer !== null) win.clearTimeout(timer); }
      if (!response.ok) throw failure('request-failed', Number(response.status) || 0);
      return response;
    }

    async function request(path, options) {
      const response = await fetchResponse(path, options);
      try { return await response.json(); }
      catch (_) { throw failure('invalid-response'); }
    }

    async function cartSnapshot() {
      return retryRead(async function () {
        const response = await fetchResponse(cartUrl + '.js');
        let cart;
        try { cart = await response.clone().json(); }
        catch (_) { throw failure('invalid-response'); }
        if (!cart || !Array.isArray(cart.items)) throw failure('invalid-response');
        return { response: response, cart: cart };
      });
    }

    // Only key + absolute quantity is safe to replay after checking server truth.
    // Adds, line indices, variant IDs and property changes are never replayed.
    async function changeWithRecovery(send, intent) {
      for (let attempt = 0; ; attempt += 1) {
        let response;
        try { response = await send(attempt); }
        catch (_) { response = null; }
        if (response && response.ok) return response;
        const status = response ? Number(response.status) : 0;
        if (response && ![400, 404, 408, 422, 429].includes(status) && status < 500) return response;
        const snapshot = await cartSnapshot();
        const item = snapshot.cart.items.find(line => line.key === intent.id);
        if (!item || item.quantity === intent.quantity) return snapshot.response;
        if (response && ![408, 429].includes(status) && status < 500) return response;
        if (attempt >= 1) throw failure('network');
        await pause(attempt);
      }
    }

    function inspectBody(input, options) {
      const classify = function (body) {
        if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
        const keys = Object.keys(body);
        const metadata = keys.length > 0 && keys.every(key => key === 'attributes' || key === 'note') &&
          (!('note' in body) || body.note === null || typeof body.note === 'string') &&
          (!('attributes' in body) || (body.attributes && typeof body.attributes === 'object' && !Array.isArray(body.attributes)));
        const change = keys.length === 2 && keys.includes('id') && keys.includes('quantity') &&
          typeof body.id === 'string' && body.id.includes(':') && Number.isSafeInteger(body.quantity) && body.quantity >= 0;
        return { metadata: metadata, change: change ? body : null };
      };
      if (options && Object.prototype.hasOwnProperty.call(options, 'body')) {
        try { return Promise.resolve(classify(JSON.parse(options.body))); } catch (_) { return Promise.resolve({}); }
      }
      if (input && typeof input.clone === 'function') {
        try { return input.clone().json().then(classify, function () { return {}; }); } catch (_) {}
      }
      return Promise.resolve({});
    }

    const io = {
      readState: function () { return request(cartUrl + '?view=kook-deposit'); },
      writeUpdates: function (updates) {
        return request(cartUrl + '/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      }
    };
    const enqueue = createQueue(function (active) { busy = active; display(); });

    async function reconcile(seed) {
      let result;
      for (let attempt = 0; ; attempt += 1) {
        try {
          result = await reconcileCart(io, seed);
          break;
        } catch (problem) {
          // A write may have committed before its response/read failed. Recover
          // from a fresh state rather than replaying an old deposit payload.
          seed = undefined;
          needsRefresh = true;
          if (attempt >= 2 || !transient(problem)) throw problem;
          await pause(attempt);
        }
      }
      error = null;
      if (result.state.cartSignature) verifiedSignature = JSON.stringify(result.state.cartSignature);
      return result;
    }

    function refresh() { doc.dispatchEvent(new win.CustomEvent('cart:build')); }
    function signature(cart) {
      return JSON.stringify([cart.currency, cart.total_price, cart.items.map(item => [item.key, item.quantity, item.original_line_price])]);
    }

    function sync(seed) {
      if (checkoutPending) {
        checkoutDirty = true;
        return Promise.resolve(null);
      }
      return enqueue(async function () {
        try {
          const result = await reconcile(seed);
          if (result.changed || needsRefresh) {
            refresh();
            needsRefresh = false;
          }
          return result;
        } catch (failure) { report(failure); throw failure; }
      });
    }

    // Keep all AJAX cart writes in one queue, including an add still in flight
    // when a customer clicks checkout. Internal deposit requests use nativeFetch.
    function isCartMutation(input, options) {
      const method = String((options && options.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'GET' || method === 'HEAD') return false;
      try {
        const url = new win.URL(typeof input === 'string' || input instanceof win.URL ? input : input.url, win.location.href);
        return url.origin === win.location.origin && new RegExp('^' + cartUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '/(add|change|update|clear)(\\.js)?/?$').test(url.pathname);
      } catch (_) { return false; }
    }

    win.fetch = function (input, options) {
      if (!isCartMutation(input, options)) return nativeFetch(input, options);
      const pendingAtCall = checkoutPending;
      const details = inspectBody(input, options);
      cartRevision += 1;
      let replayInput = input;
      if (input && typeof input.clone === 'function') {
        try { replayInput = input.clone(); } catch (_) { replayInput = null; }
      }
      return enqueue(async function () {
        const info = await details;
        const url = new win.URL(typeof input === 'string' || input instanceof win.URL ? input : input.url, win.location.href);
        const metadata = info.metadata && /\/update(?:\.js)?\/?$/.test(url.pathname);
        if (pendingAtCall && !metadata) throw new Error('checkout-pending');
        let response;
        try {
          response = info.change && replayInput && /\/change(?:\.js)?\/?$/.test(url.pathname) ?
            await changeWithRecovery(attempt => nativeFetch(attempt ? replayInput : input, options), info.change) : await nativeFetch(input, options);
        } catch (problem) {
          if (!metadata) {
            try { await reconcile(); refresh(); } catch (recovery) { report(recovery); }
          }
          throw problem;
        }
        if (metadata) {
          if (needsRefresh && !error) refresh();
          return response;
        }
        if (response.ok) {
          try {
            await reconcile();
            needsRefresh = false;
          } catch (failure) { report(failure); }
          // The product may already have been added even if deposit verification
          // failed. Return its real response and show recovery in the cart.
          refresh();
        }
        else refresh();
        return response;
      });
    };

    // Extend native APIs without editing the vendor theme bundle.
    const cartApi = win.theme && win.theme.cart;
    if (cartApi && cartApi.changeItem) {
      const originalChange = cartApi.changeItem;
      cartApi.changeItem = function () {
        if (checkoutPending) return Promise.reject(new Error('checkout-pending'));
        return originalChange.apply(this, arguments);
      };
    }

    const cartForm = win.theme && win.theme.CartForm;
    if (cartForm && cartApi && cartApi.getCartProductMarkup) {
      const originalParse = cartForm.prototype._parseProductHTML;
      const originalMarkup = cartForm.prototype.cartMarkup;
      let verifiedMarkup = null;
      function matches(form, text) {
        if (typeof text !== 'string' || !text.trim()) throw failure('markup-unavailable');
        let parsed;
        try { parsed = originalParse.call(form, text); } catch (_) { throw failure('markup-unavailable'); }
        if (!parsed.items) throw failure('markup-unavailable');
        if (!verifiedSignature) return false;
        const subtotal = Number(parsed.items.dataset.cartSubtotal);
        if (!Number.isSafeInteger(subtotal) || subtotal < 0) throw failure('markup-unavailable');
        const lines = Array.from(parsed.items.querySelectorAll('.cart__item[data-key]')).map(function (item) {
          const quantity = item.querySelector('[name="updates[]"]');
          const value = Number(quantity && quantity.value);
          if (!quantity || !item.dataset.key || !Number.isSafeInteger(value) || value < 1) throw failure('markup-unavailable');
          return [item.dataset.key, value];
        });
        return JSON.stringify([subtotal, lines]) === verifiedSignature;
      }
      if (originalParse) cartForm.prototype._parseProductHTML = function (text) {
        // A native request may already have bound the original cartMarkup before
        // this adapter loaded. Its dynamic parser still has to reject old HTML.
        try {
          if (matches(this, text)) {
            verifiedMarkup = text;
            return originalParse.call(this, text);
          }
        } catch (_) {}
        needsRefresh = true;
        this.buildCart();
        if (verifiedMarkup) {
          try { if (matches(this, verifiedMarkup)) return originalParse.call(this, verifiedMarkup); } catch (_) {}
        }
        const current = this.products && this.products.querySelector('.cart__items');
        const discounts = this.discounts && this.discounts.firstElementChild;
        if (current) {
          const wrapper = doc.createElement('div');
          wrapper.appendChild(current.cloneNode(true));
          try {
            if (!verifiedSignature || matches(this, wrapper.innerHTML)) {
              return { items: current.cloneNode(true), discounts: discounts ? discounts.cloneNode(true) : doc.createElement('div') };
            }
          } catch (_) {}
        }
        // An empty drawer has no visible snapshot to preserve. Keep known counts
        // and totals, but show loading text instead of inventing/storing old rows.
        const snapshot = verifiedSignature && JSON.parse(verifiedSignature);
        const items = doc.createElement('div');
        items.className = 'cart__items';
        items.dataset.count = snapshot ? snapshot[1].reduce((sum, line) => sum + line[1], 0) : 0;
        items.dataset.cartSubtotal = snapshot ? snapshot[0] : 0;
        items.dataset.cartSavings = 0;
        const loading = doc.createElement('p');
        loading.setAttribute('role', 'status');
        loading.textContent = 'Updating your cart…';
        items.appendChild(loading);
        return { items: items, discounts: doc.createElement('div') };
      };
      if (originalParse && originalMarkup) cartForm.prototype.cartMarkup = function (text) {
        // Late native requests must never replace the current DOM with an old
        // snapshot, including the first response when there is no cached markup.
        let valid = false;
        try { valid = matches(this, text); } catch (_) {}
        if (!valid) {
          needsRefresh = true;
          this.buildCart();
          return;
        }
        verifiedMarkup = text;
        originalMarkup.call(this, text);
        if (checkoutPending) lockCartControls(true);
        display();
      };
      cartForm.prototype.buildCart = function () {
        const form = this;
        const revision = (form.kookRevision || 0) + 1;
        form.kookRevision = revision;
        return enqueue(async function () {
          const stateRevision = cartRevision;
          try {
            if (revision !== form.kookRevision) return;
            for (let attempt = 0; attempt < 3; attempt += 1) {
              if (!verifiedSignature || needsRefresh) await reconcile();
              const markup = await retryRead(async function () {
                // The native helper discards HTTP status and swallows failures.
                // Keep its locale-aware view, with bounded safe GET recovery.
                let text;
                const response = await fetchResponse(cartUrl + '?view=ajax');
                try { text = await response.text(); } catch (_) { throw failure('invalid-response'); }
                matches(form, text); // Validate structure here, before leaving the retry.
                return text;
              });
              if (revision !== form.kookRevision || stateRevision !== cartRevision) {
                needsRefresh = true;
                return;
              }
              if (matches(form, markup)) {
                verifiedMarkup = markup;
                originalMarkup.call(form, markup);
                needsRefresh = false;
                if (checkoutPending) lockCartControls(true);
                display();
                return;
              }
              // An app using XMLHttpRequest can change the cart outside fetch.
              // Re-read current server truth inside this transaction; never await
              // another queued rebuild from inside its own queue task.
              await reconcile();
            }
            throw failure('stale-markup');
          } catch (problem) {
            if (revision === form.kookRevision && stateRevision === cartRevision) report(problem, 'markup');
          }
        });
      };
    }

    // Cart-icon clicks use theme.Drawers directly. Suppress only the automatic
    // CartDrawer opening after adds, preserving native click/focus behavior.
    if (win.theme && win.theme.CartDrawer) win.theme.CartDrawer.prototype.open = function () {};
    doc.addEventListener('cart:open', function (event) { event.stopImmediatePropagation(); }, true);

    doc.addEventListener('cart:updated', function (event) {
      const cart = event.detail && event.detail.cart;
      let nextSignature = '';
      if (cart && Array.isArray(cart.items)) {
        nextSignature = signature(cart);
        if ((nextSignature === lastCartSignature && !error) || syncingSignatures.has(nextSignature)) return;
        syncingSignatures.add(nextSignature);
      }
      sync().then(function (result) {
        if (result && nextSignature) lastCartSignature = nextSignature;
      }).catch(function () {}).finally(function () { syncingSignatures.delete(nextSignature); });
    });
    doc.addEventListener('product-form:cart:update', function () { sync().catch(function () {}); });
    doc.addEventListener('ajaxProduct:added', function () { sync().catch(function () {}); });
    win.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        releaseCheckout();
        sync().catch(function () {});
      }
    });
    win.addEventListener('online', function () {
      if (error || needsRefresh) sync().catch(function () {});
    });
    doc.addEventListener('click', function (event) {
      const retry = event.target.closest('[data-kook-deposit-retry]');
      if (retry) { sync().catch(function () {}); return; }
      const remove = event.target.closest('[data-kook-cart-remove]');
      if (checkoutPending && event.target.closest('form[data-location] .kook-cart-quantity a, form[data-location] .js-qty__adjust')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (remove && remove.closest('form[data-location]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const key = remove.getAttribute('data-kook-cart-remove');
        if (removingKeys.has(key)) return;
        removingKeys.add(key);
        cartRevision += 1;
        enqueue(async function () {
          try {
            const options = {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            };
            const response = await changeWithRecovery(() => nativeFetch(cartUrl + '/change.js', options), { id: key, quantity: 0 });
            if (!response.ok) throw failure('request-failed', Number(response.status) || 0);
            await reconcile();
            needsRefresh = false;
            refresh();
          } catch (problem) { report(problem); }
          finally { removingKeys.delete(key); }
        });
      }
    }, true);

    doc.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form.matches('form[data-location]') || !form.querySelector(checkoutSelector)) return;
      if (resumingForm === form) {
        resumingForm = null;
        resumedEvent = event;
        return;
      }
      if (event.defaultPrevented) return;
      const submitter = event.submitter || form.querySelector(checkoutSelector);
      if (event.submitter && !event.submitter.matches(checkoutSelector)) return;
      const terms = form.querySelector('.cart__terms-checkbox');
      if (terms && !terms.checked) return; // Preserve the native terms confirmation.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      lockCartControls(true);
      function verify() {
        return enqueue(async function () {
          try {
            let result;
            for (let attempt = 0; attempt < 3; attempt += 1) {
              checkoutDirty = false;
              result = await reconcile();
              if (result.changed || needsRefresh) {
                refresh();
                needsRefresh = false;
              }
              if (!checkoutDirty) return result;
            }
            throw new Error('not-settled');
          }
          catch (failure) { report(failure); throw failure; }
        });
      }
      verify().then(async function () {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          await enqueue.idle();
          if (error) throw new Error('not-ready');
          if (!checkoutDirty) return;
          await verify();
        }
        report(new Error('not-settled'));
        throw new Error('not-settled');
      }).then(function () {
        if (submitter && submitter.disabled) {
          releaseCheckout();
          return;
        }
        // Cart state is verified. Stale quantity fields must not overwrite it on POST.
        const quantityInputs = Array.from(form.querySelectorAll('[name="updates[]"]'));
        quantityInputs.forEach(function (input) {
          checkoutInputStates.set(input, input.disabled);
          input.disabled = true;
        });
        resumingForm = form;
        resumedEvent = null;
        try {
          form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined);
        } finally {
          resumingForm = null;
          if (!resumedEvent || resumedEvent.defaultPrevented) {
            releaseCheckout();
          }
        }
      }).catch(function () { releaseCheckout(); });
    }, true);

    try { sync(JSON.parse(seedElement.textContent)).catch(function () {}); }
    catch (_) { sync().catch(function () {}); }
  }

  return { selectTier: selectTier, buildUpdates: buildUpdates, reconcileCart: reconcileCart, createQueue: createQueue, start: start };
});
